import AdminLayout from "../../layouts/AdminLayout";
import useAuthStore from "../../store/authStore";
import ManagerLayout from "../../layouts/ManagerLayout";
import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { getOverrides, allowOverride, actionHolidayWorkRequest } from "../../api/attendanceService";
import apiClient from "../../api/apiClient";

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

function formatDateDisplay(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function formatTimeLocal(isoStr) {
  if (!isoStr) return "—";
  // If it's already a formatted string like "11:24 AM" (e.g., from old cached data), just return it
  if (!isoStr.includes("T")) return isoStr;
  const d = new Date(isoStr);
  return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
}

function CalendarPopup({ blockedDays, selectedId, onSelect, onClose }) {
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());

  const blockedMap = {};
  blockedDays.forEach((d) => { blockedMap[d.date] = d; });

  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  function toDateStr(day) {
    const m = String(viewMonth + 1).padStart(2, "0");
    const dd = String(day).padStart(2, "0");
    return `${viewYear}-${m}-${dd}`;
  }

  function prevMonth() {
    if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11); }
    else setViewMonth(m => m - 1);
  }
  function nextMonth() {
    if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0); }
    else setViewMonth(m => m + 1);
  }

  return (
    <div className="absolute top-full mt-2 left-0 z-50 w-72 sm:w-80 bg-white border border-gray-200 rounded-2xl shadow-2xl p-3 sm:p-4 select-none">
      <div className="flex items-center justify-between mb-3">
        <button type="button" onClick={prevMonth}
          className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-800 flex items-center justify-center transition shadow-sm border border-gray-300 font-bold text-sm">
          ‹
        </button>
        <span className="text-gray-900 font-bold text-xs sm:text-sm">{MONTH_NAMES[viewMonth]} {viewYear}</span>
        <button type="button" onClick={nextMonth}
          className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-800 flex items-center justify-center transition shadow-sm border border-gray-300 font-bold text-sm">
          ›
        </button>
      </div>

      <div className="grid grid-cols-7 mb-1">
        {DAY_LABELS.map(d => (
          <div key={d} className="text-center text-gray-500 text-[10px] sm:text-xs font-semibold py-1">{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-y-1">
        {cells.map((day, idx) => {
          if (!day) return <div key={`empty-${idx}`} />;
          const dateStr = toDateStr(day);
          const blocked = blockedMap[dateStr];
          const isSelected = blocked && String(blocked.id) === String(selectedId);

          if (blocked) {
            const isHoliday = blocked.status === "HOLIDAY";
            return (
              <button
                key={dateStr}
                type="button"
                onClick={() => { onSelect(blocked); onClose(); }}
                title={`${blocked.status}${blocked.description ? " — " + blocked.description : ""}`}
                className={`w-full aspect-square rounded-lg text-[9px] sm:text-xs font-bold flex flex-col items-center justify-center transition
                  ${isSelected
                    ? "bg-emerald-500 text-white ring-2 ring-emerald-300"
                    : isHoliday
                      ? "bg-red-100 text-red-700 border border-red-300 hover:bg-red-200"
                      : "bg-yellow-100 text-yellow-700 border border-yellow-300 hover:bg-yellow-200"
                  }`}
              >
                <span>{day}</span>
                <span className={`text-[7px] sm:text-[8px] leading-none font-bold ${isSelected ? "text-white/80" : isHoliday ? "text-red-500" : "text-yellow-600"}`}>
                  {isHoliday ? "HOL" : "WOF"}
                </span>
              </button>
            );
          }

          return (
            <div key={dateStr}
              className="w-full aspect-square rounded-lg text-[9px] sm:text-xs text-gray-700 font-medium flex items-center justify-center cursor-not-allowed hover:bg-gray-50">
              {day}
            </div>
          );
        })}
      </div>

      <div className="mt-3 flex items-center gap-3 sm:gap-4 pt-3 border-t border-gray-200 flex-wrap">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-red-200 border border-red-300" />
          <span className="text-gray-500 text-[10px] sm:text-xs">Holiday</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-yellow-200 border border-yellow-300" />
          <span className="text-gray-500 text-[10px] sm:text-xs">Week Off</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-emerald-500" />
          <span className="text-gray-500 text-[10px] sm:text-xs">Selected</span>
        </div>
      </div>

      <button type="button" onClick={onClose}
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-800 text-lg leading-none font-bold">
        ✕
      </button>
    </div>
  );
}

function RoleBadge({ role }) {
  if (role === "MANAGER")
    return <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-purple-500/30 text-purple-200 border border-purple-400/30">MGR</span>;
  if (role === "ADMIN")
    return <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-500/30 text-blue-200 border border-blue-400/30">ADMIN</span>;
  return null;
}

function AllowOverride() {
  const role = useAuthStore((state) => state.role);
  const loggedInEmployeeId = useAuthStore((state) => state.employeeId);
  const isManager = role === "MANAGER";
  const Layout = isManager ? ManagerLayout : AdminLayout;

  const [selectedDay, setSelectedDay] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const calendarRef = useRef(null);
  const [selectedManagerId, setSelectedManagerId] = useState(isManager ? loggedInEmployeeId : "ALL");
  const [checkedEmployees, setCheckedEmployees] = useState([]);
  const [remarks, setRemarks] = useState("");
  const [blockedDays, setBlockedDays] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [managers, setManagers] = useState([]);
  const [overrideList, setOverrideList] = useState([]);
  const [removeId, setRemoveId] = useState(null);
  const [loading, setLoading] = useState(false);
  // ── DIFF 1: comp-off eligible state ──────────────────────────────────────────
  const [compOffEligible, setCompOffEligible] = useState(true);

  // ── Active tab state ─────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState("grant"); // "grant" | "requests"

  // ── Holiday Work Requests state ──────────────────────────────────────────
  const [workRequests, setWorkRequests] = useState([]);
  const [workRequestsLoading, setWorkRequestsLoading] = useState(false);
  const [workRequestsPendingCount, setWorkRequestsPendingCount] = useState(0);
  const [reqActionLoading, setReqActionLoading] = useState(null); // request id being actioned
  const [reqReviewNote, setReqReviewNote] = useState({});
  const [reqFilterStatus, setReqFilterStatus] = useState("PENDING");

  // ── Existing Overrides Filter State ──────────────────────────────────────
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();
  const [overrideMonth, setOverrideMonth] = useState(currentMonth); // 0-11 or "ALL"
  const [overrideYear, setOverrideYear] = useState(currentYear); // YYYY or "ALL"

  useEffect(() => {
    function handle(e) {
      if (calendarRef.current && !calendarRef.current.contains(e.target)) setCalendarOpen(false);
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  const fetchData = async () => {
    try {
      const res = await getOverrides();
      setBlockedDays(res.data.blocked_days || []);
      const allEmps = res.data.employee_list || [];
      setEmployees(allEmps);

      const nameById = {};
      allEmps.forEach((emp) => { nameById[emp.employee_id] = emp.name; });

      const mgrMap = {};
      allEmps.forEach((emp) => {
        if (emp.role === "MANAGER" || emp.role === "ADMIN") {
          if (!mgrMap[emp.employee_id]) mgrMap[emp.employee_id] = emp.name;
        }
        if (emp.manager && emp.manager !== emp.employee_id) {
          if (!mgrMap[emp.manager]) mgrMap[emp.manager] = nameById[emp.manager] || emp.manager;
        }
      });
      const mgrs = Object.entries(mgrMap).map(([id, name]) => ({ id, name }));
      mgrs.sort((a, b) => a.name.localeCompare(b.name));
      setManagers(mgrs);
      setOverrideList(res.data.override_list || []);
    } catch (err) {
      console.error(err);
      alert("Failed to load override data");
    }
  };

  useEffect(() => { fetchData(); }, []);

  const fetchWorkRequests = async () => {
    setWorkRequestsLoading(true);
    try {
      // Use apiClient directly (bypass dedupGet cache) so we always get fresh data
      const params = reqFilterStatus !== "ALL" ? { status: reqFilterStatus } : {};
      const res = await apiClient.get("/attendance/api/holiday-work-requests/", { params });
      setWorkRequests(res.data.requests || []);
      setWorkRequestsPendingCount(res.data.pending_count || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setWorkRequestsLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, reqFilterStatus]);

  const handleWorkRequestAction = async (requestId, action) => {
    setReqActionLoading(requestId);
    try {
      await actionHolidayWorkRequest({
        action,
        request_id: requestId,
        review_note: reqReviewNote[requestId] || "",
      });
      // Immediately refetch the requests list (fresh, no cache)
      await fetchWorkRequests();
      // Notify sidebars to refresh their badge counts
      window.dispatchEvent(new CustomEvent("holiday-work-request-updated"));
    } catch (err) {
      alert(err?.response?.data?.error || "Action failed.");
    } finally {
      setReqActionLoading(null);
    }
  };

  const filteredEmployees = (
    selectedManagerId === "ALL"
      ? employees
      : employees.filter((emp) => emp.manager === selectedManagerId || emp.employee_id === selectedManagerId)
  ).slice().sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  const allChecked = filteredEmployees.length > 0 && filteredEmployees.every((emp) => checkedEmployees.includes(emp.employee_id));

  const toggleAll = () => {
    if (allChecked) {
      setCheckedEmployees((prev) => prev.filter((id) => !filteredEmployees.find((e) => e.employee_id === id)));
    } else {
      const newIds = filteredEmployees.map((e) => e.employee_id).filter((id) => !checkedEmployees.includes(id));
      setCheckedEmployees((prev) => [...prev, ...newIds]);
    }
  };

  const toggleEmployee = (empId) => {
    setCheckedEmployees((prev) => prev.includes(empId) ? prev.filter((id) => id !== empId) : [...prev, empId]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedDay) { alert("Please select a holiday/week-off date from the calendar."); return; }
    if (checkedEmployees.length === 0) { alert("Please select at least one employee."); return; }
    try {
      setLoading(true);
      // ── DIFF 2: pass comp_off_eligible in the API call ───────────────────
      const res = await allowOverride({
        action: "allow_override",
        calendar_day_id: selectedDay.id,
        employee_ids: checkedEmployees,
        remarks,
        comp_off_eligible: compOffEligible,   // ← NEW
      });
      const created = res.data?.created_for || [];
      const skipped = res.data?.skipped || [];
      let msg = "Override process completed.\n";
      if (created.length > 0) msg += `✅ Created for: ${created.join(", ")}\n`;
      if (skipped.length > 0) msg += `⚠️ Skipped: ${skipped.join(", ")}`;
      alert(msg.trim());
      setSelectedDay(null);
      setCheckedEmployees([]);
      setRemarks("");
      setSelectedManagerId(isManager ? loggedInEmployeeId : "ALL");
      // ── DIFF 4: reset compOffEligible on form reset ──────────────────────
      setCompOffEligible(true);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.error || err.response?.data?.warning || "Failed to create override");
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async () => {
    try {
      await allowOverride({ action: "remove_override", override_id: removeId });
      alert("Override removed ✅");
      setRemoveId(null);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.error || "Remove failed");
    }
  };

  const statusBadge = (s) =>
    s === "HOLIDAY"
      ? "bg-red-500/30 text-red-200 border-red-400/30"
      : "bg-yellow-500/30 text-yellow-200 border-yellow-400/30";

  const step1Done = !!selectedDay;
  const step2Done = checkedEmployees.length > 0;

  return (
    <Layout>
      <div className="p-3 sm:p-6">
        <div className="w-full max-w-[1400px] mx-auto">

          {/* HEADER */}
          <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8">
            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg flex-shrink-0">
              <span className="text-2xl sm:text-3xl">✅</span>
            </div>
            <div>
              <h1 className="text-xl sm:text-3xl font-bold text-white">Allow Check-IN</h1>
              <p className="text-white/60 text-xs sm:text-sm">Grant overrides or review holiday work requests</p>
            </div>
          </motion.div>

          {/* TABS */}
          <div className="flex items-center gap-2 mb-6">
            <button
              onClick={() => setActiveTab("grant")}
              className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
                activeTab === "grant"
                  ? "bg-emerald-500/20 border border-emerald-400/40 text-emerald-300"
                  : "text-white/50 hover:text-white hover:bg-white/10"
              }`}
            >
              🗓️ Grant Override
            </button>
            <button
              onClick={() => setActiveTab("requests")}
              className={`relative px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
                activeTab === "requests"
                  ? "bg-amber-500/20 border border-amber-400/40 text-amber-300"
                  : "text-white/50 hover:text-white hover:bg-white/10"
              }`}
            >
              📋 Pending Requests
              {workRequestsPendingCount > 0 && (
                <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-amber-500 rounded-full animate-pulse">
                  {workRequestsPendingCount}
                </span>
              )}
            </button>
          </div>

          {/* GRANT OVERRIDE TAB */}
          {activeTab === "grant" && (<>

          {/* STEP INDICATORS — scrollable on mobile */}
          <div className="flex items-center gap-2 sm:gap-4 mb-5 sm:mb-6 overflow-x-auto pb-1">
            {[
              { num: 1, label: "Select Date", done: step1Done },
              { num: 2, label: "Select Employees", done: step2Done },
              { num: 3, label: "Allow Override", done: false },
            ].map((step, i) => (
              <div key={step.num} className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
                <div className={`w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center text-[10px] sm:text-xs font-bold border-2 transition-all ${step.done ? "bg-emerald-500 border-emerald-400 text-white" : "bg-white/10 border-white/20 text-white/50"}`}>
                  {step.done ? "✓" : step.num}
                </div>
                <span className={`text-xs sm:text-sm font-medium whitespace-nowrap ${step.done ? "text-emerald-300" : "text-white/40"}`}>{step.label}</span>
                {i < 2 && <div className="w-5 sm:w-8 h-px bg-white/20 mx-0.5 sm:mx-1" />}
              </div>
            ))}
          </div>

          {/* PENDING REQUESTS TAB */}
          </>)}
          {activeTab === "requests" && (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl p-5 sm:p-8 mb-6 sm:mb-8">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                <h2 className="text-lg font-bold text-white">Holiday Work Requests</h2>
                <div className="flex items-center gap-2">
                  {["PENDING", "APPROVED", "REJECTED", "ALL"].map(s => (
                    <button key={s} onClick={() => setReqFilterStatus(s)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                        reqFilterStatus === s
                          ? s === "PENDING" ? "bg-amber-500/30 text-amber-200 border border-amber-400/40"
                            : s === "APPROVED" ? "bg-emerald-500/30 text-emerald-200 border border-emerald-400/40"
                            : s === "REJECTED" ? "bg-red-500/30 text-red-200 border border-red-400/40"
                            : "bg-white/20 text-white border border-white/30"
                          : "text-white/40 hover:text-white hover:bg-white/10"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                  <button onClick={fetchWorkRequests}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white/50 hover:text-white hover:bg-white/10 transition-all">
                    🔄 Refresh
                  </button>
                </div>
              </div>

              {workRequestsLoading ? (
                <div className="flex justify-center py-12">
                  <div className="w-7 h-7 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : workRequests.length === 0 ? (
                <div className="text-center py-12 text-white/40">
                  <p className="text-3xl mb-2">📭</p>
                  <p className="text-sm">No {reqFilterStatus !== "ALL" ? reqFilterStatus.toLowerCase() : ""} requests found.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {workRequests.map(req => (
                    <div key={req.id}
                      className="rounded-xl border p-4"
                      style={{
                        background: req.status === "PENDING" ? "rgba(245,158,11,0.07)"
                          : req.status === "APPROVED" ? "rgba(16,185,129,0.07)"
                          : "rgba(239,68,68,0.07)",
                        borderColor: req.status === "PENDING" ? "rgba(245,158,11,0.3)"
                          : req.status === "APPROVED" ? "rgba(16,185,129,0.3)"
                          : "rgba(239,68,68,0.3)",
                      }}
                    >
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="space-y-1">
                          <p className="text-white font-semibold text-sm">{req.employee_name} <span className="text-white/40 text-xs font-normal">({req.employee_id})</span></p>
                          <p className="text-white/60 text-xs">📅 {req.date} &nbsp;·&nbsp;
                            <span className={`font-semibold ${req.day_type === "HOLIDAY" ? "text-purple-300" : "text-blue-300"}`}>
                              {req.day_label}{req.day_desc ? ` — ${req.day_desc}` : ""}
                            </span>
                          </p>
                          <p className="text-white/50 text-xs">
                            🕐 Check-in: {formatTimeLocal(req.check_in)} &nbsp;·&nbsp; 🕔 Check-out: {formatTimeLocal(req.check_out)}
                            {req.working_hours ? ` (${req.working_hours}h)` : ""}
                          </p>
                          {req.employee_note && (
                            <p className="text-white/40 text-xs italic">Note: {req.employee_note}</p>
                          )}
                          {req.status !== "PENDING" && (
                            <p className="text-white/40 text-xs">
                              {req.status === "APPROVED" ? "✅" : "❌"} {req.status} by {req.reviewed_by || "—"}
                              {req.review_note ? ` — "${req.review_note}"` : ""}
                            </p>
                          )}
                        </div>
                        {req.status === "PENDING" && (
                          <div className="flex flex-col gap-2 min-w-[180px]">
                            <input
                              type="text"
                              placeholder="Review note (optional)"
                              value={reqReviewNote[req.id] || ""}
                              onChange={e => setReqReviewNote(prev => ({ ...prev, [req.id]: e.target.value }))}
                              className="w-full px-2.5 py-1.5 rounded-lg text-xs text-white placeholder-white/30 bg-white/10 border border-white/20 focus:outline-none focus:border-white/40"
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleWorkRequestAction(req.id, "approve")}
                                disabled={reqActionLoading === req.id}
                                className="flex-1 py-1.5 rounded-lg text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 transition"
                              >
                                {reqActionLoading === req.id ? "…" : "✅ Approve"}
                              </button>
                              <button
                                onClick={() => handleWorkRequestAction(req.id, "reject")}
                                disabled={reqActionLoading === req.id}
                                className="flex-1 py-1.5 rounded-lg text-xs font-bold text-white bg-red-600 hover:bg-red-500 disabled:opacity-50 transition"
                              >
                                {reqActionLoading === req.id ? "…" : "❌ Reject"}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {activeTab === "grant" && (
          <>
          {/* FORM CARD */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl p-5 sm:p-8 mb-6 sm:mb-8">
            <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">

              {/* STEP 1: DATE PICKER */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center text-[10px] sm:text-xs font-bold border-2 ${step1Done ? "bg-emerald-500 border-emerald-400 text-white" : "bg-white/10 border-white/30 text-white/60"}`}>
                    {step1Done ? "✓" : "1"}
                  </div>
                  <label className="text-white font-semibold text-sm">Select Holiday / Week-Off Date</label>
                </div>

                <div className="relative" ref={calendarRef}>
                  <button
                    type="button"
                    onClick={() => setCalendarOpen((o) => !o)}
                    className={`w-full flex items-center justify-between bg-white/10 border rounded-xl px-3 sm:px-4 py-3 text-left transition text-sm ${selectedDay ? "border-emerald-400/60 bg-emerald-500/5" : "border-white/30 hover:border-white/50"}`}
                  >
                    <span className={`${selectedDay ? "text-white font-semibold" : "text-white/40"} text-xs sm:text-sm truncate pr-2`}>
                      {selectedDay
                        ? `${formatDateDisplay(selectedDay.date)} — ${selectedDay.status === "HOLIDAY" ? "Holiday" : "Week Off"}${selectedDay.description ? ` (${selectedDay.description})` : ""}`
                        : "Click to open calendar and pick a blocked date"}
                    </span>
                    <span className="text-white/60 text-lg sm:text-xl ml-2 flex-shrink-0">📅</span>
                  </button>

                  <AnimatePresence>
                    {calendarOpen && (
                      <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.15 }}>
                        <CalendarPopup
                          blockedDays={blockedDays}
                          selectedId={selectedDay?.id}
                          onSelect={(day) => { setSelectedDay(day); }}
                          onClose={() => setCalendarOpen(false)}
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {selectedDay && (
                  <div className={`mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold border ${statusBadge(selectedDay.status)}`}>
                    <span>{selectedDay.status === "HOLIDAY" ? "🎉 Holiday" : "📴 Week Off"}</span>
                    {selectedDay.description && <span className="opacity-70">— {selectedDay.description}</span>}
                    {selectedDay.location_name && selectedDay.location_name !== "All Locations" && (
                      <span className="text-blue-300/70">📍 {selectedDay.location_name}</span>
                    )}
                    <button type="button" onClick={() => setSelectedDay(null)} className="ml-1 opacity-60 hover:opacity-100 transition">✕</button>
                  </div>
                )}
              </div>

              {/* STEP 2: MANAGER FILTER + EMPLOYEES */}
              <div className={`transition-all ${!step1Done ? "opacity-40 pointer-events-none" : ""}`}>
                <div className="flex items-center gap-2 mb-4">
                  <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center text-[10px] sm:text-xs font-bold border-2 ${step2Done ? "bg-emerald-500 border-emerald-400 text-white" : "bg-white/10 border-white/30 text-white/60"}`}>
                    {step2Done ? "✓" : "2"}
                  </div>
                  <label className="text-white font-semibold text-sm">
                    {isManager ? "Select Employees" : "Select Manager & Employees"}
                  </label>
                  {!step1Done && <span className="text-white/30 text-xs ml-1">(select a date first)</span>}
                </div>

                {isManager ? (
                  <div className="mb-4 flex items-center gap-3 px-4 py-3 rounded-xl bg-indigo-500/10 border border-indigo-400/30">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-xs font-bold">👔</span>
                    </div>
                    <div>
                      <p className="text-white/50 text-[10px] uppercase font-bold tracking-wide">Showing employees under</p>
                      <p className="text-white font-semibold text-sm">My Team</p>
                    </div>
                  </div>
                ) : (
                  <div className="mb-4">
                    <label className="block text-white/70 text-xs font-medium mb-2">👔 Filter by Manager</label>
                    <select
                      value={selectedManagerId}
                      onChange={(e) => setSelectedManagerId(e.target.value)}
                      className="w-full bg-white/10 border border-white/30 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-blue-400 transition"
                    >
                      <option value="ALL" className="bg-gray-800">👥 All — Show All Employees & Managers</option>
                      {managers.map((mgr) => (
                        <option key={mgr.id} value={mgr.id} className="bg-gray-800">{mgr.name} ({mgr.id})</option>
                      ))}
                    </select>
                    <p className="text-white/30 text-xs mt-1">Switching manager filter preserves existing selections.</p>
                  </div>
                )}

                {/* Employee checkboxes */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white/70 text-xs font-medium">
                      👷 Employees
                      {checkedEmployees.length > 0 && (
                        <span className="ml-2 px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 text-xs font-bold border border-emerald-400/30">
                          {checkedEmployees.length} selected
                        </span>
                      )}
                    </span>
                    {filteredEmployees.length > 0 && (
                      <button
                        type="button"
                        onClick={toggleAll}
                        className={`text-xs font-bold px-3 py-1.5 rounded-lg border transition ${allChecked ? "bg-emerald-500/20 text-emerald-300 border-emerald-400/30 hover:bg-emerald-500/30" : "bg-white/10 text-white/70 border-white/20 hover:bg-white/20"}`}
                      >
                        {allChecked ? "✓ Deselect All" : "☐ Select All"}
                      </button>
                    )}
                  </div>

                  {filteredEmployees.length === 0 ? (
                    <div className="bg-white/5 border border-white/10 rounded-xl px-4 py-6 text-center text-white/40 text-sm">
                      No employees found for the selected manager.
                    </div>
                  ) : (
                    <div
                      className="bg-white/5 border border-white/10 rounded-xl overflow-hidden max-h-56 sm:max-h-64 overflow-y-auto"
                      style={{ scrollbarWidth: "thin", scrollbarColor: "rgba(255,255,255,0.15) transparent" }}
                    >
                      {filteredEmployees.map((emp, idx) => {
                        const isChecked = checkedEmployees.includes(emp.employee_id);
                        const avatarColors = emp.role === "MANAGER" ? "from-purple-500 to-indigo-500" : "from-blue-500 to-cyan-500";
                        return (
                          <label
                            key={emp.employee_id}
                            className={`flex items-center gap-3 px-3 sm:px-4 py-3 cursor-pointer transition ${idx !== 0 ? "border-t border-white/5" : ""} ${isChecked ? "bg-emerald-500/10" : "hover:bg-white/5"}`}
                          >
                            <div
                              onClick={() => toggleEmployee(emp.employee_id)}
                              className={`w-5 h-5 rounded flex-shrink-0 flex items-center justify-center border-2 transition ${isChecked ? "bg-emerald-500 border-emerald-400" : "bg-white/10 border-white/30"}`}
                            >
                              {isChecked && (
                                <svg className="w-3 h-3 text-white" viewBox="0 0 12 12" fill="none">
                                  <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              )}
                            </div>
                            <input type="checkbox" className="hidden" checked={isChecked} onChange={() => toggleEmployee(emp.employee_id)} />
                            <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br ${avatarColors} flex items-center justify-center flex-shrink-0`}>
                              <span className="text-white text-[10px] sm:text-xs font-bold">
                                {emp.name?.split(" ").map((n) => n[0]).join("").slice(0, 2)}
                              </span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1">
                                <p className="text-white text-xs sm:text-sm font-semibold truncate">{emp.name}</p>
                                <RoleBadge role={emp.role} />
                              </div>
                              <p className="text-white/40 text-[10px] sm:text-xs">
                                {emp.employee_id}
                                {emp.location_name && <span className="ml-2 text-blue-300/60">📍 {emp.location_name}</span>}
                              </p>
                            </div>
                            {isChecked && <span className="text-emerald-400 text-xs font-bold flex-shrink-0">✓</span>}
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* STEP 3: REMARKS + SUBMIT */}
              <div className={`transition-all ${!step1Done || !step2Done ? "opacity-40 pointer-events-none" : ""}`}>
                <div className="flex items-center gap-2 mb-4">
                  <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center text-[10px] sm:text-xs font-bold border-2 ${step1Done && step2Done ? "bg-white/20 border-white/30 text-white" : "bg-white/10 border-white/20 text-white/40"}`}>
                    3
                  </div>
                  <label className="text-white font-semibold text-sm">
                    Remarks & Submit
                  </label>
                  {(!step1Done || !step2Done) && (
                    <span className="text-white/30 text-xs ml-1">({!step1Done ? "select a date first" : "select employees first"})</span>
                  )}
                </div>

                {/* ── DIFF 3: Comp-off toggle ───────────────────────────────────────── */}
                <div className="flex items-center justify-between mb-4 px-1">
                  <div>
                    <p className="text-white font-semibold text-sm">🎁 Grant Comp-Off</p>
                    <p className="text-white/40 text-xs mt-0.5">
                      Credit 1 comp-off day per employee for working on this{" "}
                      {selectedDay?.status === "HOLIDAY" ? "holiday" : "week-off"}.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setCompOffEligible(v => !v)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${compOffEligible ? "bg-emerald-500" : "bg-white/20"
                      }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${compOffEligible ? "translate-x-6" : "translate-x-1"
                        }`}
                    />
                  </button>
                </div>

                <textarea
                  rows="3"
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="Reason for allowing work on holiday/week-off (optional)..."
                  className="w-full bg-white/10 border border-white/30 rounded-xl px-4 py-3 text-white resize-none focus:outline-none focus:border-emerald-400 transition placeholder-white/30 mb-4 text-sm"
                />

                {step1Done && step2Done && (
                  <div className="bg-white/5 border border-white/10 rounded-xl p-3 sm:p-4 mb-4 text-xs sm:text-sm space-y-1">
                    <p className="text-white/60">
                      <span className="text-white font-semibold">Date:</span>{" "}
                      {formatDateDisplay(selectedDay.date)} —{" "}
                      <span className={selectedDay.status === "HOLIDAY" ? "text-red-300" : "text-yellow-300"}>
                        {selectedDay.status === "HOLIDAY" ? "Holiday" : "Week Off"}
                      </span>
                    </p>
                    <p className="text-white/60">
                      <span className="text-white font-semibold">Selected:</span>{" "}
                      {checkedEmployees.length} person{checkedEmployees.length !== 1 ? "s" : ""}
                    </p>
                    <p className="text-white/60">
                      <span className="text-white font-semibold">Comp-Off:</span>{" "}
                      <span className={compOffEligible ? "text-emerald-300" : "text-white/40"}>
                        {compOffEligible ? "✅ Will be granted" : "❌ Not granted"}
                      </span>
                    </p>
                  </div>
                )}

                <div className="flex justify-center">
                  <button
                    type="submit"
                    disabled={loading || !step1Done || !step2Done}
                    className={`w-full sm:w-auto px-6 sm:px-10 py-3 rounded-xl font-bold shadow-lg transition-all text-sm ${loading || !step1Done || !step2Done ? "bg-white/10 text-white/40 cursor-not-allowed" : "bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:from-emerald-600 hover:to-teal-600 hover:scale-105"}`}
                  >
                    {loading ? "Processing..." : `✅ Allow Override${checkedEmployees.length > 0 ? ` (${checkedEmployees.length})` : ""}`}
                  </button>
                </div>
              </div>
            </form>
          </motion.div>

          {/* EXISTING OVERRIDES TABLE */}
          {(() => {
            const filteredOverrideList = overrideList.filter((o) => {
              if (overrideMonth === "ALL" && overrideYear === "ALL") return true;
              const d = new Date(o.date);
              const m = d.getMonth();
              const y = d.getFullYear();
              const matchMonth = overrideMonth === "ALL" || m === parseInt(overrideMonth);
              const matchYear = overrideYear === "ALL" || y === parseInt(overrideYear);
              return matchMonth && matchYear;
            });

            return (
              overrideList.length > 0 && (
                <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
                  className="bg-white/10 backdrop-blur-xl rounded-2xl overflow-hidden border border-white/20 shadow-2xl">
                  <div className="px-4 sm:px-6 py-4 bg-white/10 border-b border-white/20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <h3 className="text-lg sm:text-xl font-bold text-white">Existing Overrides</h3>
                      <span className="px-3 py-1 bg-emerald-500/20 text-emerald-300 text-xs font-bold rounded-full border border-emerald-400/30">
                        {filteredOverrideList.length} total
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <select
                        value={overrideMonth}
                        onChange={(e) => setOverrideMonth(e.target.value)}
                        className="bg-white/10 border border-white/30 rounded-lg px-3 py-1.5 text-white text-xs sm:text-sm focus:outline-none focus:border-blue-400"
                      >
                        <option value="ALL" className="bg-gray-800">All Months</option>
                        {MONTH_NAMES.map((m, i) => (
                          <option key={m} value={i} className="bg-gray-800">{m}</option>
                        ))}
                      </select>
                      <select
                        value={overrideYear}
                        onChange={(e) => setOverrideYear(e.target.value)}
                        className="bg-white/10 border border-white/30 rounded-lg px-3 py-1.5 text-white text-xs sm:text-sm focus:outline-none focus:border-blue-400"
                      >
                        <option value="ALL" className="bg-gray-800">All Years</option>
                        {[currentYear - 2, currentYear - 1, currentYear, currentYear + 1].map((y) => (
                          <option key={y} value={y} className="bg-gray-800">{y}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-xs sm:text-sm text-center min-w-[600px]">
                      <thead>
                        <tr className="bg-white/10">
                      <th className="py-3 sm:py-4 px-3 sm:px-4 text-white/70 font-semibold">Date</th>
                      <th className="px-3 sm:px-4 text-white/70 font-semibold">Employee / Manager</th>
                      <th className="px-3 sm:px-4 text-white/70 font-semibold">Location</th>
                      <th className="px-3 sm:px-4 text-white/70 font-semibold">Status</th>
                      <th className="px-3 sm:px-4 text-white/70 font-semibold">Remarks</th>
                      <th className="px-3 sm:px-4 text-white/70 font-semibold">Action</th>
                    </tr>
                      </thead>
                      <tbody>
                        {filteredOverrideList.length === 0 ? (
                          <tr>
                            <td colSpan="6" className="py-8 text-white/50 text-sm italic">
                              No overrides found for the selected month and year.
                            </td>
                          </tr>
                        ) : (
                          filteredOverrideList.map((o) => (
                            <tr key={o.id} className="border-b border-white/10 hover:bg-white/5 transition">
                              <td className="py-3 sm:py-4 px-3 sm:px-4 text-white/80 font-medium whitespace-nowrap">{formatDateDisplay(o.date)}</td>
                        <td className="px-3 sm:px-4">
                          <div className="flex items-center gap-2 justify-center">
                            <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center flex-shrink-0">
                              <span className="text-white text-[10px] sm:text-xs font-bold">
                                {o.employee_name?.split(" ").map((n) => n[0]).join("").slice(0, 2)}
                              </span>
                            </div>
                            <div className="text-left">
                              <p className="text-white font-semibold text-xs sm:text-sm">{o.employee_name}</p>
                              <p className="text-white/40 text-[10px]">{o.employee_id}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 sm:px-4">
                          <span className="px-2 py-1 rounded-lg bg-blue-500/20 text-blue-200 border border-blue-400/30 text-xs">{o.location_name || "—"}</span>
                        </td>
                        <td className="px-3 sm:px-4">
                          <span className={`px-2 sm:px-3 py-1 rounded-lg text-xs border font-semibold ${statusBadge(o.status)}`}>
                            {o.status === "HOLIDAY" ? "Holiday" : "Week Off"}
                          </span>
                        </td>
                        <td className="px-3 sm:px-4 text-white/60 italic text-xs max-w-[120px] sm:max-w-[180px] truncate">{o.reason || "—"}</td>
                        <td className="px-3 sm:px-4">
                          <button
                            onClick={() => setRemoveId(o.id)}
                            className="px-3 sm:px-4 py-1.5 sm:py-2 bg-red-500 hover:bg-red-600 rounded-lg text-white text-xs font-bold transition"
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </motion.div>
              )
            );
          })()}
          </>
          )}
        </div>
      </div>

      {/* REMOVE CONFIRM MODAL */}
      <AnimatePresence>
        {removeId && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              className="bg-slate-900 border border-white/20 rounded-2xl p-5 sm:p-6 w-full max-w-md shadow-2xl">
              <h4 className="text-white font-bold text-base sm:text-lg mb-2">Remove Override?</h4>
              <p className="text-white/50 text-sm mb-5 sm:mb-6">This will revoke the person's ability to check in on that holiday/week-off.</p>
              <div className="flex justify-end gap-3">
                <button onClick={() => setRemoveId(null)} className="px-4 py-2 border border-white/20 text-white/70 rounded-xl hover:bg-white/10 transition text-sm">Cancel</button>
                <button onClick={handleRemove} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl transition text-sm">Remove Override</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Layout>
  );
}

export default AllowOverride;
